import type { FC } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
// @dynamic-i18n-keys
// Dynamic t() at line 140 (studio.modals.batchEdit.actions.${action}; action from BatchAction):
//   studio.modals.batchEdit.actions.edit_title
//   studio.modals.batchEdit.actions.edit_tags
//   studio.modals.batchEdit.actions.edit_description
//   studio.modals.batchEdit.actions.edit_gallery
//   studio.modals.batchEdit.actions.edit_tier
//   studio.modals.batchEdit.actions.edit_schedule
//   studio.modals.batchEdit.actions.edit_license
//   studio.modals.batchEdit.actions.edit_display_options
//   studio.modals.batchEdit.actions.edit_comments
//   studio.modals.batchEdit.actions.edit_labels
//   studio.modals.batchEdit.actions.edit_preset
//   studio.modals.batchEdit.actions.bool_ai_add
//   studio.modals.batchEdit.actions.bool_ai_del
//   studio.modals.batchEdit.actions.bool_noai_add
//   studio.modals.batchEdit.actions.bool_noai_del
//   studio.modals.batchEdit.actions.bool_mature_add
//   studio.modals.batchEdit.actions.bool_mature_del
//   studio.modals.batchEdit.actions.other_delete
//   studio.modals.batchEdit.actions.other_unpublish
//   studio.modals.batchEdit.actions.other_move_to_folder
//   studio.modals.batchEdit.actions.other_create_folder_from_selection
//   studio.modals.batchEdit.actions.other_merge
//   studio.modals.batchEdit.actions.other_move_to_stash
//   studio.modals.batchEdit.actions.other_move_to_drafts
//   studio.modals.batchEdit.actions.other_sell
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import ListCard from '@wix/da-ds/pkg/List/ListCard';
import BatchActionItem from './_partials/BatchActionItem';
import { BatchAction, StudioSection } from '../../types/store';
import { useEnabledFlagList } from '@wix/da-react-context/pkg/flags/hooks';

import s from './MobileBatchEdit.scss';

export interface Props {
  activeSection: StudioSection;
  selectedCount: number;
  selectedBatchAction?: BatchAction;
  selectBatchAction: (batchAction: BatchAction) => void;
  cancelBatchAction: () => void;
  requestClose: () => void;
}

const BATCH_ACTION_ITEMS = [
  { action: BatchAction.EDIT_TITLE, excludeSection: [StudioSection.JOURNALS] },
  {
    action: BatchAction.EDIT_TAGS,
    excludeSection: [
      StudioSection.JOURNALS,
      StudioSection.APPS,
      StudioSection.APPS_UNLISTED,
    ],
  },
  {
    action: BatchAction.EDIT_DESCRIPTION,
    excludeSection: [StudioSection.JOURNALS],
  },
  {
    action: BatchAction.EDIT_GALLERY,
    excludeSection: [StudioSection.JOURNALS],
  },
  {
    action: BatchAction.EDIT_TIER,
    withCoreSymbol: true,
    excludeSection: [StudioSection.APPS, StudioSection.APPS_UNLISTED],
  },
  {
    action: BatchAction.EDIT_SCHEDULE,
    section: [
      StudioSection.DRAFTS,
      StudioSection.LITERATURE,
      StudioSection.JOURNALS,
      StudioSection.SCHEDULED,
      StudioSection.STASH,
      StudioSection.PLAYGROUND,
      StudioSection.PRODUCTS_PCP_UNLISTED,
      StudioSection.PRODUCTS_EXCLUSIVE_UNLISTED,
    ],
    withCoreSymbol: true,
  },
  {
    action: BatchAction.EDIT_DISPLAY_OPTIONS,
    excludeSection: [
      StudioSection.JOURNALS,
      StudioSection.APPS,
      StudioSection.APPS_UNLISTED,
    ],
  },
  {
    action: BatchAction.EDIT_LICENSE,
    excludeSection: [
      StudioSection.JOURNALS,
      StudioSection.APPS,
      StudioSection.APPS_UNLISTED,
    ],
  },
  {
    action: BatchAction.EDIT_COMMENTS,
    excludeSection: [StudioSection.JOURNALS, StudioSection.APPS],
  },
  {
    action: BatchAction.EDIT_LABELS,
    excludeSection: [
      StudioSection.JOURNALS,
      StudioSection.APPS,
      StudioSection.APPS_UNLISTED,
    ],
  },
  {
    action: BatchAction.EDIT_PRESET,
    section: [
      StudioSection.DRAFTS,
      StudioSection.LITERATURE,
      StudioSection.SCHEDULED,
      StudioSection.STASH,
      StudioSection.PLAYGROUND,
    ],
  },
];

const MobileBatchEdit: FC<Props> = ({
  activeSection,
  selectedCount,
  selectedBatchAction,
  selectBatchAction,
  cancelBatchAction,
  requestClose,
}) => {
  const { t } = useTranslation();
  const enabledFlags = useEnabledFlagList();
  const filterBySection = useCallback(
    item =>
      (!item.section || item.section.includes(activeSection)) &&
      (!item.excludeSection || !item.excludeSection.includes(activeSection)) &&
      (!item.feature || enabledFlags.includes(item.feature)) &&
      (!item.feature_exclude || !enabledFlags.includes(item.feature_exclude)),
    [activeSection, enabledFlags]
  );

  return (
    <Modal
      className={s['wrapper']}
      layoutClassName={s['layout']}
      contentClassName={s['content']}
      heading={t('studio.modals.batchEdit.title', { count: selectedCount })}
      onRequestClose={() => {
        cancelBatchAction();
        requestClose();
      }}
      showHeaderBorder
      showFooterBorder
      shouldCloseOnOverlayClick
      isOpen
      isFullscreen
      withScrollableContentWrapper
    >
      <ListCard className={s['list-container']}>
        {BATCH_ACTION_ITEMS.filter(filterBySection).map(
          ({ action, withCoreSymbol }) => (
            <BatchActionItem
              key={`action-${action}`}
              action={action}
              label={t(`studio.modals.batchEdit.actions.${action}`)}
              isExpanded={selectedBatchAction === action}
              onExpand={expand =>
                expand ? selectBatchAction(action) : cancelBatchAction()
              }
              withCoreSymbol={withCoreSymbol}
            />
          )
        )}
      </ListCard>
    </Modal>
  );
};
MobileBatchEdit.displayName = 'MobileBatchEdit';

export default MobileBatchEdit;
